import {defineStore}             from "pinia"
import {ref, readonly, computed} from "vue"
import UserConfigService         from "@/api/UserConfigService"
import AuthService               from "@/api/AuthService"
import {useNotificationStore}    from "@/stores/notification"

export interface UserPermissions {
  canAccessResourcePlanning: boolean
}

export interface UserInfo {
  id: number | null
  cprId: number | null
  name: string | null
  permissions: UserPermissions
}

export const useUserStore = defineStore("user", () => {
  const notificationStore = useNotificationStore()

  const userConfigService = new UserConfigService()
  const authService = new AuthService()

  const config = ref()
  const userInfo = ref<UserInfo>({
    id: null,
    cprId: null,
    name: null,
    permissions: {
      canAccessResourcePlanning: false
    }
  })
  const isLoadingUserInfo = ref(false)

  const getDefaultConfig = () => {
    return {selectedExtendedFilterId: null}
  }

  config.value = getDefaultConfig()

  const loadUserConfig = () => {
    userConfigService.getConfig("task-item").then(data => {
      if (data.success) {
        config.value = data.data
      } else {
        notificationStore.setErrorNotification(data.data.message || "Ein Fehler ist aufgetreten!")
        config.value = getDefaultConfig()
      }
    })
  }

  const loadUserInfo = async () => {
    if (isLoadingUserInfo.value) {
      return
    }

    isLoadingUserInfo.value = true
    try {
      const response = await authService.getUserInfo()
      if (response.success && response.data) {
        // Extract user info from companyPersonRelations if available
        const userRelation = response.data.companyPersonRelations?.[0]

        userInfo.value = {
          id: response.data.id || null,
          cprId: userRelation?.id || null,
          name: userRelation?.name || null,
          permissions: {
            canAccessResourcePlanning: response.data.permissions?.canAccessResourcePlanning || false
          }
        }
      } else {
        console.error("Failed to load user info:", response.error || response.message)
      }
    } catch (error) {
      console.error("Error loading user info:", error)
    } finally {
      isLoadingUserInfo.value = false
    }
  }

  const hasPermission = (permission: keyof UserPermissions): boolean => {
    return userInfo.value.permissions[permission] || false
  }

  const canAccessResourcePlanning = computed(() => hasPermission("canAccessResourcePlanning"))

  const resetUserData = () => {
    userInfo.value = {
      id: null,
      cprId: null,
      name: null,
      permissions: {
        canAccessResourcePlanning: false
      }
    }
  }

  return {
    config,
    userInfo: readonly(userInfo),
    isLoadingUserInfo: readonly(isLoadingUserInfo),
    canAccessResourcePlanning,
    loadUserConfig,
    loadUserInfo,
    hasPermission,
    resetUserData
  }
})
